// enum.extension.ts
const METADATA_ENUM_KEY: unique symbol = Symbol('metadata');

class EnumUtil<T> {
	public readonly Enum: T;

	private constructor(Enum: T, metaDataObject?: Partial<Record<keyof T, any>>) {
		this.Enum = Enum;
		Object.assign(this, Enum);
		Object.freeze(this);

		// if	metaDataObject is provided, add it to the EnumUtil instance
		if (metaDataObject) {
			Object.keys(metaDataObject).forEach(key => {
				Reflect.defineMetadata(METADATA_ENUM_KEY.valueOf(), metaDataObject[key], this, key);
			});
		}
	}

	public static bind<T extends object>(Enum: T, metaDataObject?: Partial<Record<keyof T, any>>): T & EnumUtil<T> {
		return new EnumUtil(Enum, metaDataObject) as T & EnumUtil<T>;
	}

	public getEnumKeyByValue(value: string | number): string | undefined {
		return Object.keys(this.Enum).find(key => this.Enum[key as keyof T] === value);
	}

	public getEnumValues(): string[] {
		return Object.values(this.Enum) as string[];
	}

	public getEnumKeys(): string[] {
		return Object.keys(this.Enum).filter(key => isNaN(Number(key)));
	}

	public getEnumValueByKey(key: string): string | undefined {
		return this.Enum[key as keyof T] as string | undefined;
	}
}

// type helper that extract onlu public members of an object or	class
declare type Public<T> = {
	[K in keyof T]: T[K];
};

// type	helper that extract only the properties of an object (not methods or private members)
declare type OnlyPropsOf<T> = {
	[K in keyof T]: T[K] extends Function ? never : K;
};

export type EnumType<T extends object> = Public<enumType<T>> & string;

/**
 * @description Extends an enum object with utility methods
 * @param Enum
 * @param metaDataObject
 * @constructor
 * @example
 * ```typescript
 * enum EColor {
 * 	Red = "RED",
 * 	Green = "GREEN",
 * 	Blue = "BLUE"
 * }
 *
 * // הוספת ההרחבות ל-enum
 * export const ExtendedColor = EnumExt(EColor);
 *
 * // שימוש בפונקציות החדשות
 * const colorName = ExtendedColor.getEnumKeyByValue("RED"); // "Red"
 * console.log(colorName);
 *
 * const colorValues = ExtendedColor.getEnumValues(); // ["RED", "GREEN", "BLUE"]
 * console.log(colorValues);
 *
 * const colorKeys = ExtendedColor.getEnumKeys(); // ["Red", "Green", "Blue"]
 * console.log(colorKeys);
 *
 * const colorValue = ExtendedColor.getEnumValueByKey("Red"); // "RED"
 * console.log(colorValue);
 * ```
 * @example 2
 * ```typescript
 * enum EColor {
 * 	Red = 'RED',
 * 	Green = 'GREEN',
 * 	Blue = 'BLUE',
 * }
 *
 * // הוספת ההרחבות ל-enum
 * export const ExtendedColor = EnumExt(EColor);
 *
 * // usage color as enum type:
 * type UserPalette = {
 * 	mainColor: EnumType<EColor>;
 * };
 *
 * const userPalette: UserPalette = {
 * 	mainColor: ExtendedColor.Red,
 * };
 * ```
 */
export function EnumExt<T extends object>(Enum: T, metaDataObject?: Partial<Record<keyof T, any>>): T & Public<EnumUtil<T>> & string {
	return EnumUtil.bind(Enum, metaDataObject) as T & Public<EnumUtil<T>> & string;
}
